import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { FlowItem } from '../../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { PassOption } from './multipass-post-action-copy-modal';

export const useMultipassPostActionCopy = (currentPassName: string) => {
  const { t } = useTranslation();
  const { values, setFieldValue } =
    useFormikContext<BeaconWeldingData.MultipassSettingsFormData>();

  const availablePasses = useMemo((): PassOption[] => {
    const passes: PassOption[] = [];
    const numberOfPasses = values.schedule?.length || 0;

    // Add root pass if current pass is not root pass
    if (!currentPassName.includes('rootPassPostActions')) {
      passes.push({
        id: 'rootPass',
        label: t('welding:multipass_root_pass'),
        fieldName: 'rootPassPostActions',
        hasExistingActions: (values.rootPassPostActions || []).length > 0,
      });
    }

    // Add all other passes
    for (let i = 0; i < numberOfPasses; i++) {
      const passFieldName = `schedule[${i}].postActions`;
      if (passFieldName !== currentPassName) {
        const passNumber = i + 2;
        const passActions = values.schedule?.[i]?.postActions || [];
        passes.push({
          id: `pass-${i}`,
          label: t('common:pass_with_number', { number: passNumber }),
          fieldName: passFieldName,
          hasExistingActions: passActions.length > 0,
        });
      }
    }

    return passes;
  }, [values.rootPassPostActions, values.schedule, currentPassName, t]);

  const currentPassLabel = useMemo(() => {
    if (currentPassName.includes('rootPassPostActions')) {
      return t('welding:multipass_root_pass');
    }

    const match = currentPassName.match(/schedule\[(\d+)\]\.postActions/);
    if (match) {
      const passIndex = parseInt(match[1], 10);
      const passNumber = passIndex + 2;
      return t('common:pass_with_number', { number: passNumber });
    }

    return t('common:pass');
  }, [currentPassName, t]);

  const copyPostActionsToSelectedPasses = useCallback(
    (selectedPasses: PassOption[]) => {
      // Get the current pass post actions
      const currentPostActions = getCurrentPostActions(values, currentPassName);

      if (!currentPostActions || currentPostActions.length === 0) {
        return;
      }

      // Apply to selected passes (strip IDs to avoid duplicates)
      selectedPasses.forEach((pass) => {
        const copiedActions = currentPostActions.map(({ id, ...item }) => ({
          ...item,
          id: uuid(),
        }));
        setFieldValue(pass.fieldName, copiedActions);
      });
    },
    [values, currentPassName, setFieldValue]
  );

  return {
    availablePasses,
    currentPassLabel,
    copyPostActionsToSelectedPasses,
  };
};

function getCurrentPostActions(
  values: BeaconWeldingData.MultipassSettingsFormData,
  fieldName: string
): FlowItem[] | undefined {
  if (fieldName.includes('rootPassPostActions')) {
    return values.rootPassPostActions;
  }

  // Extract pass index from field name like "schedule[0].postActions"
  const match = fieldName.match(/schedule\[(\d+)\]\.postActions/);
  if (match) {
    const passIndex = parseInt(match[1], 10);
    return values.schedule?.[passIndex]?.postActions;
  }

  return undefined;
}
